import * as React from 'react';
import styled from 'styled-components';
import { ResponsiveBar } from '@nivo/bar';
import { Tooltip as InfoHint } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { MONTH_FULL_LIST } from '../../constants';
import { FranchiseType } from '../../api/franchise';
import { SelectInputWithSelectAll } from '../../atoms';
import { convertDataKeysToLabels } from '../../utils/convertWidgetValues';
import { BRANCH_SALES_GRAPH_DESCRIPTION } from '../../constants/graphDescriptions';

const SALES_KEYS = ['Sales', 'Projected Sales'];
const SALES_COLOR = '#a7e0f4'; // theme.marker1
const PROJECTED_COLOR = '#dfb9c9'; // theme.marker2

const formatCurrency = (value: number | string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatCurrencyShort = (value: number | string) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000)
    return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
};

const FranchiseSalesGraphWidget = ({
  data,
  franchises,
  onSelectChange,
}: Props) => {
  const [
    selectedIds,
    setSelectedIds,
  ] = React.useState<number[] | null>(null);

  const hasData =
    !!data &&
    data.length > 0 &&
    data.some(
      d =>
        Number(d.sales) !== 0 ||
        Number(d.projectedSales) !== 0,
    );

  const selectedFranchises = React.useMemo(() => {
    if (!selectedIds || !franchises) return [];
    const idSet = new Set(selectedIds);
    return franchises.filter(f => idSet.has(f.id));
  }, [selectedIds, franchises]);

  const allSelected =
    selectedFranchises.length > 0 &&
    franchises &&
    selectedFranchises.length === franchises.length;

  return (
    <Container>
      <Header>
        Branch Sales Graph
        <InfoHint
          arrow
          placement="top"
          title={BRANCH_SALES_GRAPH_DESCRIPTION}
        >
          <InfoIcon fontSize="small" />
        </InfoHint>
      </Header>
      {franchises && franchises.length > 0 && (
        <SelectContainer>
          <SelectInputWithSelectAll
            data={franchises}
            valueKey="id"
            labelKey="name"
            onMenuClose={(selected, isCanceled) => {
              if (!isCanceled) {
                setSelectedIds(
                  selected?.map((item: any) => item.id) ||
                    null,
                );
              }
              if (onSelectChange) {
                onSelectChange(selected, isCanceled);
              }
            }}
            placeholder="Select branches from the list..."
          />
        </SelectContainer>
      )}

      <SelectionBar>
        <SelectionLabel>Showing</SelectionLabel>
        {allSelected ||
        !selectedFranchises ||
        selectedFranchises.length === 0 ? (
          <AllChip>All branches</AllChip>
        ) : (
          <ChipRow>
            {selectedFranchises.slice(0, 4).map(f => (
              <Chip key={f.id} title={f.name}>
                {f.name}
              </Chip>
            ))}
            {selectedFranchises.length > 4 && (
              <ChipMore
                title={selectedFranchises
                  .slice(4)
                  .map(f => f.name)
                  .join(', ')}
              >
                +{selectedFranchises.length - 4} more
              </ChipMore>
            )}
          </ChipRow>
        )}
      </SelectionBar>

      {hasData ? (
        <ChartArea>
          <ChartCanvas>
          <ResponsiveBar
            data={convertDataKeysToLabels(data)}
            keys={SALES_KEYS}
            indexBy="Month"
            groupMode="grouped"
            padding={0.32}
            innerPadding={3}
            borderRadius={4}
            colors={[SALES_COLOR, PROJECTED_COLOR]}
            valueScale={{ type: 'linear' }}
            enableLabel={false}
            enableGridY
            gridYValues={5}
            margin={{
              top: 32,
              right: 24,
              bottom: 70,
              left: 64,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 8,
              tickValues: 5,
              format: (v: any) => formatCurrencyShort(v),
            }}
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
              format: (v: any) =>
                `${MONTH_FULL_LIST[v - 1]?.slice(0, 3)}'${data
                  .find(item => item.month === v)
                  ?.year.toString()
                  .substr(-2)}`,
            }}
            theme={{
              fontFamily:
                "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif",
              textColor: '#555c6a',
              fontSize: 12,
              axis: {
                ticks: {
                  text: {
                    fill: '#79808f',
                    fontSize: 11,
                    fontWeight: 500,
                  },
                },
              },
              grid: {
                line: {
                  stroke: '#e6e9ef',
                  strokeWidth: 1,
                  strokeDasharray: '3 4',
                },
              },
              legends: {
                text: {
                  fill: '#3b424e',
                  fontSize: 12,
                  fontWeight: 500,
                },
              },
              tooltip: {
                container: {
                  background: 'transparent',
                  boxShadow: 'none',
                  padding: 0,
                  border: 'none',
                },
              },
            }}
            tooltip={({
              id,
              value,
              color,
              data: bar,
            }: any) => {
              const numericValue = Number(
                value !== undefined && value !== null
                  ? value
                  : bar?.[id],
              );
              return (
                <TooltipBox>
                  <TooltipMonth>
                    {MONTH_FULL_LIST[Number(bar.Month) - 1]}{' '}
                    {bar.Year}
                  </TooltipMonth>
                  <TooltipRow>
                    <Swatch
                      style={{ backgroundColor: color }}
                    />
                    <span>{id}</span>
                    <strong>
                      {formatCurrency(
                        Number.isFinite(numericValue)
                          ? numericValue
                          : 0,
                      )}
                    </strong>
                  </TooltipRow>
                </TooltipBox>
              );
            }}
            animate
            motionStiffness={120}
            motionDamping={16}
          />
          </ChartCanvas>
          <ChartLegend>
            <ChartLegendItem>
              <LegendSwatch
                style={{ background: SALES_COLOR }}
              />
              <span>Sales</span>
            </ChartLegendItem>
            <ChartLegendItem>
              <LegendSwatch
                style={{ background: PROJECTED_COLOR }}
              />
              <span>Projected Sales</span>
            </ChartLegendItem>
          </ChartLegend>
        </ChartArea>
      ) : (
        <EmptyState>
          <EmptyTitle>No sales in this period</EmptyTitle>
          <EmptySubtitle>
            Try selecting different branches or check back
            once invoices land in the current 6-month window.
          </EmptySubtitle>
        </EmptyState>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SelectContainer = styled.div`
  margin: 16px 0 8px;
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 4px 0 16px;
  padding: 10px 12px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const SelectionLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  flex-shrink: 0;
`;

const ChipRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100);
  font-size: 0.75rem;
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipMore = styled(Chip)`
  background: var(--neutral-100);
  color: var(--neutral-700);
  border-color: var(--neutral-200);
  cursor: help;
`;

const AllChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-size: 0.75rem;
  font-weight: 600;
`;

const ChartArea = styled.div`
  height: 400px;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ChartCanvas = styled.div`
  flex: 1;
  min-height: 0;
`;

const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  flex-wrap: wrap;
  padding: 8px 0 0;
  font-size: 0.8125rem;
  color: var(--neutral-700);
`;

const ChartLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > strong {
    color: var(--neutral-900);
    font-weight: 700;
  }
`;

const LegendSwatch = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin: 0;
`;

const InfoIcon = styled(InfoOutlinedIcon)`
  color: var(--neutral-400);
  cursor: help;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
  }
`;

const TooltipBox = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 6px 20px rgba(19, 23, 32, 0.12);
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
  font-size: 0.8125rem;
  color: var(--neutral-700);
  min-width: 180px;
`;

const TooltipMonth = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > span {
    flex: 1;
    color: var(--neutral-700);
  }

  > strong {
    color: var(--neutral-900);
    font-weight: 700;
  }
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  padding: 64px 24px;
  border: 1px dashed var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  margin-top: 8px;
`;

const EmptyTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--neutral-700);
`;

const EmptySubtitle = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  max-width: 320px;
  line-height: 1.5;
`;

interface Props {
  data: any;
  franchises: FranchiseType[];
  onSelectChange?: (
    selected: any[] | null,
    isCanceled: boolean,
  ) => void;
}

export default FranchiseSalesGraphWidget;
